import { useState, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import {
  getAllSessions,
  saveSession as persistSessionToDb,
  deleteSession as deleteSessionFromDb,
  countSessions,
} from '../../../utils/looperdb';
import { useAuth } from '../../../context/AuthContext';
import { FREE_SESSION_LIMIT } from '../audio/constants';

// IndexedDB-backed session storage for the multitrack player. Owns
// the list of sessions, the input field state, the loading flag and
// the "show sessions panel" toggle. Does NOT own the engine - the
// caller orchestrates how a fetched session is applied to the active
// multitrack state (that orchestration moves into LooperEngine in
// commit 4).
//
// Public API:
//   sessions[], sessionName/setSessionName, sessionLoading,
//   showSessions/setShowSessions
//   refresh()                      reload list from IndexedDB
//   saveSession(sessionPayload)    write to IndexedDB + refresh list
//   getById(sessionId)             find one session in the loaded list
//   removeById(sessionId)          delete + refresh list
//   checkFreeLimit()               returns true if save is allowed,
//                                  shows a toast and returns false
//                                  if the user is on the free tier
//                                  and at the FREE_SESSION_LIMIT.

export const useLooperSessions = () => {
  const { t } = useTranslation();
  const { isPremium } = useAuth();

  const [sessions, setSessions] = useState([]);
  const [sessionName, setSessionName] = useState('');
  const [sessionLoading, setSessionLoading] = useState(false);
  const [showSessions, setShowSessions] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const all = await getAllSessions();
      setSessions(all.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
    } catch (err) {
      console.error('useLooperSessions.refresh error', err);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const checkFreeLimit = useCallback(async () => {
    if (isPremium) return true;
    try {
      const count = await countSessions();
      if (count >= FREE_SESSION_LIMIT) {
        toast.error(
          t('recorder.sessionFreeLimit', {
            defaultValue: `Limite ${FREE_SESSION_LIMIT} sessioni raggiunto. Passa a PRO per sessioni illimitate!`,
            n: FREE_SESSION_LIMIT,
          })
        );
        return false;
      }
    } catch (err) {
      console.error('useLooperSessions.checkFreeLimit error', err);
    }
    return true;
  }, [isPremium, t]);

  const saveSession = useCallback(async (sessionPayload) => {
    setSessionLoading(true);
    try {
      await persistSessionToDb(sessionPayload);
      toast.success(t('recorder.sessionSaved'));
      setSessionName('');
      await refresh();
      return true;
    } catch (err) {
      console.error('useLooperSessions.saveSession error', err);
      toast.error(t('recorder.sessionSaveError'));
      return false;
    } finally {
      setSessionLoading(false);
    }
  }, [refresh, t]);

  const getById = useCallback(async (sessionId) => {
    const all = await getAllSessions();
    return all.find(s => s.id === sessionId) || null;
  }, []);

  const removeById = useCallback(async (sessionId) => {
    try {
      await deleteSessionFromDb(sessionId);
      toast.success(t('recorder.sessionDeleted'));
      await refresh();
      return true;
    } catch (err) {
      console.error('useLooperSessions.removeById error', err);
      toast.error(t('recorder.sessionDeleteError'));
      return false;
    }
  }, [refresh, t]);

  return {
    // State
    sessions,
    sessionName,
    setSessionName,
    sessionLoading,
    setSessionLoading,
    showSessions,
    setShowSessions,

    // Actions
    refresh,
    checkFreeLimit,
    saveSession,
    getById,
    removeById,
  };
};

export default useLooperSessions;
